import { useAppSelector } from 'hooks/redux';
import React, { memo } from 'react';
import { createSelector } from 'reselect';

import NextIcon from '@mui/icons-material/NavigateNextRounded';
import RepeatIcon from '@mui/icons-material/RepeatRounded';
import { Button, IconButton, Tooltip, Typography } from '@mui/material';

import { transactionsSelectors } from 'app-components/flux/transactions';
import type { SplitItemProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';
import { getTransactionById } from 'app-components/flux/transactions/transactionsSelectors';

import SplitItemsList from './SplitItemsList';
import { useSplitsViewStyles } from './splitsViewStyles';

const selectOtherSideQCSGenPayment = createSelector(
  [(_s, id: string | null | undefined) => id, transactionsSelectors.getResourcesById],
  (id, txnById) => {
    const txn = id ? txnById.get(id) : null;
    if (txn?.type === 'PAYMENT') {
      const transferId = txn?.split?.items?.find((split) => !!split.transfer?.id)?.transfer?.id;
      return transferId ? txnById.get(transferId)?.source === 'QCS_GENERATED' : false;
    }
    return false;
  },
);

function SplitsView(props: {
  splitItems: SplitItemProps[];
  setShowSplits?: React.Dispatch<React.SetStateAction<boolean>>;
  goToOtherSide?: (txn: TransactionProps) => void;
  txnId?: string | null;
  amount: number;
  headerLabel?: string;
  showRepeatIcon?: boolean;
  onEdit?: () => void;
  disabledEdit?: boolean;
  variant?: 'default' | 'series';
}) {
  const {
    splitItems,
    setShowSplits,
    goToOtherSide,
    txnId,
    amount,
    headerLabel,
    showRepeatIcon,
    onEdit,
    disabledEdit = false,
    variant = 'default',
  } = props;
  const { classes, cx } = useSplitsViewStyles();
  const isSeriesVariant = variant === 'series';

  // Disable edit if other side is a QCS generated payment or a sales receipt
  const disableSplitEdit = useAppSelector(
    (s) =>
      !!(
        txnId &&
        (selectOtherSideQCSGenPayment(s, txnId) || getTransactionById(s, txnId)?.subtype === 'SALES_RECEIPT')
      ),
  );

  // No splits assigned: render nothing (must come after hooks)
  if (!splitItems || splitItems.length === 0) return <React.Fragment />;

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
      return;
    }
    setShowSplits?.(true);
    tracker.track(tracker.events.viewSplitsV2, {
      initiator: txnId ? 'editTxnWithSplits' : 'createTxn',
      transactionAmount: amount,
      transactionAmountSign: amount >= 0 ? 'positive' : 'negative',
    });
  };

  if (disabledEdit) return <React.Fragment />;

  return (
    <div className={cx(classes.root, isSeriesVariant && classes.rootSeries)}>
      <div className={cx(classes.titleIconGroup, isSeriesVariant && classes.titleIconGroupSeries)}>
        <Typography variant='bodyRegular' className={classes.preview}>
          <span className={classes.titleWithIcon}>
            {headerLabel ?? `${splitItems.length} Splits`}
            {showRepeatIcon && (
              <Tooltip
                title='Splits were assigned by your Series.'
                placement='top'
                slotProps={{
                  tooltip: {
                    sx: {
                      width: 160,
                      maxWidth: 'none', // important if you want exact width
                    },
                  },
                }}
              >
                <RepeatIcon className={classes.repeatIcon} />
              </Tooltip>
            )}
          </span>
        </Typography>
        {isSeriesVariant ? (
          <Button
            id='edit-splits-button'
            className={classes.editButton}
            disabled={disableSplitEdit}
            onClick={handleEdit}
            endIcon={<NextIcon />}
          >
            Edit
          </Button>
        ) : (
          <IconButton
            id='edit-splits-button'
            className={classes.toggleButtons}
            disabled={disableSplitEdit}
            onClick={handleEdit}
          >
            <Typography variant='bodyRegular'>Edit</Typography>
            <NextIcon />
          </IconButton>
        )}
      </div>

      <SplitItemsList items={splitItems} txnId={txnId} goToOtherSide={goToOtherSide} variant={variant} />
    </div>
  );
}

export default memo(SplitsView);
